import contoursLeft from '@/assets/contours_left.svg';
import contoursRight from '@/assets/contours_right.svg';
import pqLogoWhite from '@/assets/pq_logo.svg';
import { useAccount } from '@/hooks/useAccount';
import { PqServiceStatus } from '@/lib/pq-client';
import { Anchor, Box, Button, Divider, Image, Stack, Text, Title } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

const PQ_WEB_LOGIN_URL = 'https://pqweb.parcelquest.com/#login';

/** Shown to accounts whose billing is managed outside the mobile app (e.g. Aviator-Mobile, County-Mobile). */
const ContactSupportBilling: React.FC = () => {
    const { refreshAccount, signOut } = useAccount();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [signingOut, setSigningOut] = useState(false);
    const [signOutError, setSignOutError] = useState<string | null>(null);

    const checkAgain = async () => {
        setChecking(true);
        setMessage(null);
        try {
            const account = await refreshAccount();
            if (account.serviceStatus === PqServiceStatus.Active) navigate('/', { replace: true });
            else setMessage('Your account is not active yet. Changes can take a few minutes to apply.');
        } catch {
            setMessage('Unable to check your account. Check your connection and try again.');
        } finally {
            setChecking(false);
        }
    };

    const handleSignOut = async () => {
        setSigningOut(true);
        setSignOutError(null);
        try {
            await signOut();
        } catch {
            setSignOutError('Unable to sign out. Please try again.');
            setSigningOut(false);
        }
    };

    return (
        <Box component="main" h="100%" bg="pq-red" pos="relative" style={{ overflow: 'hidden' }}>
            <Image h="50dvh" fit="contain" style={{ position: 'absolute', left: '-50vw', top: '-5dvh', pointerEvents: 'none', filter: 'brightness(0) invert(1)' }} src={contoursLeft} alt="" aria-hidden="true" />
            <Image w="50dvw" fit="contain" style={{ position: 'absolute', right: 0, bottom: 0, pointerEvents: 'none', filter: 'brightness(0) invert(1)' }} src={contoursRight} alt="" aria-hidden="true" />
            <Box h="100%" pos="relative" style={{ overflowY: 'auto', touchAction: 'pan-y', overscrollBehavior: 'contain' }}>
                <Stack align="stretch" justify="center" gap="xl" mih="100%" maw={440} mx="auto" px="xl" py={32} c="white">
                    <Image w="100%" h={80} fit="contain" src={pqLogoWhite} alt="ParcelQuest" />
                    <Stack gap="xs" ta="center">
                        <Title order={1} size="h2">Update your billing</Title>
                        <Text>
                           Access to PQ Mobile has been suspended due to an issue with your credit card on file. Please log in to your ParcelQuest Online account and verify your billing information.
                        </Text>
                    </Stack>
                    <Stack gap="sm">
                        {/* A new browsing context: installed apps hand it to the device's browser instead of navigating the app away. */}
                        <Button
                            component="a"
                            href={PQ_WEB_LOGIN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="lg"
                            radius="xl"
                            fullWidth
                            color="white"
                            c="pq-red"
                            rightSection={<IconExternalLink size={20} />}
                        >
                            ParcelQuest Online Account
                        </Button>
                        <Button variant="outline" color="white" radius="xl" fullWidth loading={checking} onClick={() => void checkAgain()}>
                            I've updated my billing
                        </Button>
                        {message && <Text size="sm" ta="center" role="status">{message}</Text>}
                    </Stack>
                    <Divider color="white" />
                    <Stack gap="xs" ta="center">
                        <Title order={2} size="h4">Still need help?</Title>
                        <Text size="md">
                            Email us at{' '}
                            <Anchor href="mailto:support@parcelquest.com" c="white" fw={700} underline="always">support@parcelquest.com</Anchor>
                            {' '}or{' '}
                            call us at <Anchor href="tel:+18882178999" c="white" fw={700} underline="always">(888) 217-8999</Anchor>,
                            Monday through Friday, 9:00 AM to 5:00 PM Pacific.
                        </Text>
                    </Stack>
                    <Stack gap="xs" align="center">
                        <Button variant="subtle" color="white" loading={signingOut} onClick={() => void handleSignOut()}>Sign out</Button>
                        {signOutError && <Text size="sm" ta="center" role="alert">{signOutError}</Text>}
                    </Stack>
                </Stack>
            </Box>
        </Box>
    );
};

export default ContactSupportBilling;
